import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MagnifyingGlass, Tag, Plus, DownloadSimple, PencilSimple, Trash, CircleNotch, Sparkle } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useProposals } from '../hooks/useProposals';
import { useHaptic } from '../../../hooks/useHaptic';
import { TableRowSkeleton } from '../../../components/ui/Skeleton';
import { Sheet } from '../../../components/ui/Sheet';
import { PageHeader } from '../../../components/ui/PageHeader';
import { Pagination } from '../../../components/ui/Pagination';
import { proposalService } from '../../../services/proposal.service';
import type { Proposal } from '../../../services/proposal.service';
import { ProposalFormModal } from './ProposalFormModal';
import { ProposalCompareModal } from './ProposalCompareModal';
import { ProposalDocuments } from './ProposalDocuments';
import type { DocumentReview } from './ProposalDocuments';

interface ProposalListViewProps {
  onBack: () => void;
}

const formatDate = (iso?: string) =>
  iso ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const budgetText = (p: Proposal) =>
  p.budget_lakhs != null ? `₹${p.budget_lakhs} Lakhs` : p.budget_note || 'TBD';

const piText = (p: Proposal) => p.pi || p.faculty || 'IIT Madras';

const byWhom = (iso?: string, who?: string) => (iso ? `${formatDate(iso)}${who ? ` · ${who}` : ''}` : '—');

/** Last edit, else creation (the import date for imported rows that were never edited). */
const modified = (p: Proposal) =>
  p.updated_at
    ? { date: p.updated_at, who: p.updated_by }
    : { date: p.created_at, who: p.created_by === 'import' ? 'Imported' : p.created_by };

function Detail({ label, value }: { label: string; value?: string | number | null }) {
  return (
    <div>
      <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block mb-1.5">{label}</span>
      <p className="text-xs font-bold text-slate-700 break-words">{value || value === 0 ? value : 'N/A'}</p>
    </div>
  );
}

const HIGHLIGHT_ROWS = [
  { key: 'problem', label: 'Problem' },
  { key: 'solution', label: 'Solution' },
  { key: 'impact', label: 'Impact' },
] as const;

export function ProposalListView({ onBack }: ProposalListViewProps) {
  const { mediumTap } = useHaptic();
  const limit = 20;

  const {
    searchTerm,
    proposals,
    loading,
    error,
    selectedProposal,
    setSelectedProposal,
    page,
    setPage,
    totalPages,
    totalItems,
    handleSearchChange,
    refresh,
  } = useProposals(limit);

  const [formMode, setFormMode] = useState<'create' | 'edit' | null>(null);
  const [exporting, setExporting] = useState(false);
  const navigate = useNavigate();
  const isAdmin = localStorage.getItem('role') === 'admin';
  const [deleting, setDeleting] = useState(false);

  const handleProposalClick = (p: Proposal) => {
    mediumTap();
    setSelectedProposal(p);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      await proposalService.exportXlsx(searchTerm.trim() || undefined);
    } catch {
      toast.error('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const handleDelete = async (p: Proposal) => {
    if (!p.proposal_id) return;
    if (!window.confirm(`Delete ${p.proposal_id} — "${p.title}"?\n\nThis cannot be undone. Its ID will not be reused.`)) return;
    setDeleting(true);
    try {
      const res = await proposalService.remove(p.proposal_id);
      toast.success(`${p.proposal_id} deleted`);
      if (res.index_pending) toast.warning('Deleted, but the search index could not be updated yet.');
      setSelectedProposal(null);
      refresh();
    } catch (err) {
      toast.error((err as { message?: string })?.message || 'Delete failed.');
    } finally {
      setDeleting(false);
    }
  };

  const handleSaved = (p: Proposal) => {
    refresh();
    if (formMode === 'edit') setSelectedProposal(p);
  };

  // Set by the documents block once an upload with AI is saved: open while analysing, then the result.
  const [compare, setCompare] = useState<DocumentReview | null>(null);

  // After an upload or an accepted compare: refresh the list and the open panel.
  const handleCompareSaved = (p: Proposal) => {
    refresh();
    setSelectedProposal(p);
  };

  return (
    <div className="space-y-6 font-sans">
      <PageHeader
        breadcrumb={[
          { label: 'Dashboard', onClick: onBack },
          { label: 'Proposals' },
        ]}
        title="Project Proposals"
        subtitle="Manage and track proposals submitted by PIs for fund raising."
      />

      {/* Tools Section */}
      <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
        <div className="relative w-full sm:max-w-md">
          <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400">
            <MagnifyingGlass size={16} weight="bold" />
          </span>
          <input
            type="text"
            placeholder="Search by topic, title, PI, keyword or P-ID…"
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="w-full bg-white border border-slate-200 focus:border-accent focus:ring-4 focus:ring-accent/5 outline-none rounded-xl pl-10 pr-4 py-2.5 text-xs text-slate-800 placeholder-slate-400 shadow-xs transition-smooth"
          />
        </div>
        <div className="flex items-center gap-3 w-full sm:w-auto justify-between sm:justify-end">
          <div className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">
            {searchTerm.trim() ? 'Matches' : 'Total'}: <span className="text-slate-800 font-extrabold">{totalItems}</span>
          </div>
          {isAdmin && (
            <button
              onClick={() => navigate('/proposals/ai-settings')}
              title="The AI's prompt, and the rules it learned from reviewers' corrections"
              className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl border border-slate-200 bg-white text-slate-600 font-bold text-xs hover:bg-slate-50 transition-all"
            >
              <Sparkle size={14} weight="fill" />
              AI settings
            </button>
          )}
          <button
            onClick={handleExport}
            disabled={exporting}
            className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl border border-slate-200 bg-white text-slate-600 font-bold text-xs hover:bg-slate-50 disabled:opacity-50 transition-all"
          >
            {exporting ? <CircleNotch size={14} className="animate-spin" /> : <DownloadSimple size={14} weight="bold" />}
            Export
          </button>
          <button
            onClick={() => setFormMode('create')}
            className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-accent text-white font-bold text-xs shadow-md shadow-accent/20 hover:bg-accent/90 transition-all"
          >
            <Plus size={14} weight="bold" />
            New Proposal
          </button>
        </div>
      </div>

      {/* Proposals Table Section */}
      <div className="overflow-x-auto bg-white border border-slate-200/50 rounded-2xl shadow-xs">
        <table className="w-full border-collapse text-left">
          <thead>
            <tr className="border-b border-slate-100 bg-slate-50/75 text-slate-400 text-[10px] font-bold uppercase tracking-wider">
              <th className="py-4.5 pl-6">Project ID</th>
              <th className="py-4.5 px-4">Proposal Title</th>
              <th className="py-4.5 px-4">Domain / Subdomain</th>
              <th className="py-4.5 px-4">PI / Co-PI / Department</th>
              <th className="py-4.5 px-4">Keywords / Tags</th>
              <th className="py-4.5 px-4 text-right whitespace-nowrap">Budget (Lakhs)</th>
              <th className="py-4.5 px-4 text-right whitespace-nowrap">Duration (Months)</th>
              <th className="py-4.5 px-4">Proposal Period</th>
              <th className="py-4.5 pr-6 text-right">Modified</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50 text-xs font-sans">
            {loading ? (
              <TableRowSkeleton cols={9} rows={8} />
            ) : error ? (
              <tr>
                <td colSpan={9} className="py-16 text-center text-rose-500 font-semibold">{error}</td>
              </tr>
            ) : proposals.length === 0 ? (
              <tr>
                <td colSpan={9} className="py-16 text-center text-slate-400 font-medium">No matching proposals found.</td>
              </tr>
            ) : (
              proposals.map((p) => {
                const mod = modified(p);
                return (
                  <tr
                    key={p._id}
                    onClick={() => handleProposalClick(p)}
                    className="hover:bg-slate-50/70 transition-smooth group cursor-pointer align-top"
                  >
                    <td className="py-4 pl-6 font-extrabold text-slate-500 whitespace-nowrap">{p.proposal_id}</td>
                    <td className="py-4 px-4 font-bold text-slate-800 group-hover:text-accent transition-colors min-w-56 max-w-sm">
                      <div className="line-clamp-2 leading-snug">{p.title}</div>
                    </td>
                    <td className="py-4 px-4 text-slate-600 font-medium min-w-32">
                      <div>{p.domain}</div>
                      {p.sub_domain && <div className="text-[10px] text-slate-400 mt-1">{p.sub_domain}</div>}
                    </td>
                    <td className="py-4 px-4 min-w-40">
                      <div className="font-semibold text-slate-700">{piText(p)}</div>
                      {p.co_pis && p.co_pis.length > 0 && (
                        <div className="text-[10px] text-slate-500 mt-1">Co-PI: {p.co_pis.join(', ')}</div>
                      )}
                      {p.department && <div className="text-[10px] text-slate-400 mt-1">{p.department}</div>}
                    </td>
                    <td className="py-4 px-4 min-w-36">
                      {p.keywords && p.keywords.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {p.keywords.map((k, i) => (
                            <span key={`${k}-${i}`} className="text-[9px] font-bold px-2 py-0.5 rounded-full border bg-accent-light border-accent/20 text-accent whitespace-nowrap">
                              {k}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-slate-400 font-medium">—</span>
                      )}
                    </td>
                    <td className="py-4 px-4 text-right font-extrabold text-slate-900 whitespace-nowrap">
                      {p.budget_lakhs != null ? p.budget_lakhs.toLocaleString('en-IN', { maximumFractionDigits: 2 }) : '—'}
                    </td>
                    <td className="py-4 px-4 text-right font-semibold text-slate-700 whitespace-nowrap">
                      {p.duration_months ?? '—'}
                    </td>
                    <td className="py-4 px-4 text-slate-600 font-medium min-w-28">{p.proposal_period || '—'}</td>
                    <td className="py-4 pr-6 text-right whitespace-nowrap">
                      <div className="text-slate-500 font-medium">{formatDate(mod.date)}</div>
                      {mod.who && <div className="text-[10px] text-slate-400 mt-1">{mod.who}</div>}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {!loading && !error && proposals.length > 0 && (
        <Pagination
          page={page}
          totalPages={totalPages}
          totalItems={totalItems}
          itemsPerPage={limit}
          itemLabel="proposal"
          onPageChange={(p) => setPage(p)}
        />
      )}

      {/* Detail Drawer Panel */}
      <Sheet
        isOpen={selectedProposal !== null}
        onClose={() => setSelectedProposal(null)}
        title="Proposal Details"
        maxWidth="max-w-2xl"
      >
        {selectedProposal && (
          <div className="space-y-6 font-sans">
            <div>
              <div className="flex flex-wrap items-center gap-2 mb-2">
                {selectedProposal.proposal_id && (
                  <span className="text-[9px] font-extrabold text-slate-600 bg-slate-100 border border-slate-200 px-2.5 py-1 rounded-full tracking-wider">
                    {selectedProposal.proposal_id}
                  </span>
                )}
                <span className="text-[9px] font-bold text-accent bg-accent-light border border-accent/20 px-2.5 py-1 rounded-full uppercase tracking-wider">
                  {selectedProposal.domain}
                </span>
                {selectedProposal.project_type && (
                  <span className="text-[9px] font-bold text-slate-505 bg-slate-50 border border-slate-200 px-2.5 py-1 rounded-full uppercase tracking-wider">
                    {selectedProposal.project_type}
                  </span>
                )}
              </div>
              <h3 className="font-outfit font-extrabold text-2xl text-slate-900 leading-tight">
                {selectedProposal.title}
              </h3>
              <div className="flex items-center gap-2 mt-3">
                <button
                  onClick={() => setFormMode('edit')}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-slate-200 text-slate-600 font-bold text-[11px] hover:bg-slate-50 transition-all"
                >
                  <PencilSimple size={13} weight="bold" /> Edit
                </button>
                <button
                  onClick={() => handleDelete(selectedProposal)}
                  disabled={deleting}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-rose-200 text-rose-600 font-bold text-[11px] hover:bg-rose-50 disabled:opacity-50 transition-all"
                >
                  {deleting ? <CircleNotch size={13} className="animate-spin" /> : <Trash size={13} weight="bold" />} Delete
                </button>
              </div>
            </div>

            {/* Documents first: download, add more, replace (the old file is kept under "Replaced") */}
            <ProposalDocuments
              key={selectedProposal.proposal_id || selectedProposal._id}
              proposal={selectedProposal}
              onUploaded={handleCompareSaved}
              onReview={setCompare}
            />

            {/* Attributes block */}
            <div className="grid grid-cols-2 gap-4 bg-slate-50 p-4 rounded-2xl border border-slate-200/50">
              <div>
                <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Budget Requirement</span>
                <span className="font-outfit font-extrabold text-slate-950 text-xl leading-none">
                  {budgetText(selectedProposal)}
                </span>
              </div>
              <div>
                <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Principal Investigator</span>
                <span className="font-bold text-slate-800 text-sm block truncate">{piText(selectedProposal)}</span>
                {selectedProposal.co_pis && selectedProposal.co_pis.length > 0 && (
                  <span className="text-[11px] font-semibold text-slate-500 block mt-1">
                    Co-PI: {selectedProposal.co_pis.join(', ')}
                  </span>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Detail label="Department" value={selectedProposal.department} />
              <Detail label="Sub-Domain" value={selectedProposal.sub_domain} />
              <Detail label="Duration" value={selectedProposal.duration_months ? `${selectedProposal.duration_months} months` : null} />
              <Detail label="UN SDG" value={selectedProposal.un_sdg} />
              <Detail label="Categorization" value={selectedProposal.categorization} />
              <Detail label="Proposal Period" value={selectedProposal.proposal_period} />
              <Detail label="Type of Project" value={selectedProposal.project_type} />
              <Detail label="Nature of Document" value={selectedProposal.document_nature} />
              <Detail label="Posters Available" value={selectedProposal.posters_available} />
              {selectedProposal.faculty && selectedProposal.faculty !== selectedProposal.pi && (
                <Detail label="Faculty / Centre (as imported)" value={selectedProposal.faculty} />
              )}
              {selectedProposal.source_sn && <Detail label="Original S.No" value={selectedProposal.source_sn} />}
              <Detail label="Created" value={byWhom(selectedProposal.created_at, selectedProposal.created_by)} />
              {selectedProposal.updated_by && (
                <Detail label="Last Edited" value={byWhom(selectedProposal.updated_at, selectedProposal.updated_by)} />
              )}
            </div>

            {/* Keywords */}
            {selectedProposal.keywords && selectedProposal.keywords.length > 0 && (
              <div>
                <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block mb-2">Keywords / Tags</span>
                <div className="flex flex-wrap gap-1.5">
                  {selectedProposal.keywords.map((kw, i) => (
                    <span key={i} className="text-[9px] font-bold bg-slate-100 border border-slate-200 text-slate-500 px-2.5 py-1 rounded-lg flex items-center gap-1.5">
                      <Tag size={10} />
                      {kw}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <hr className="border-slate-100" />

            {/* Summary */}
            <div className="space-y-2">
              <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block">Summary</span>
              <p className="text-slate-600 text-xs leading-relaxed whitespace-pre-wrap font-medium">
                {selectedProposal.description || 'No summary available.'}
              </p>
            </div>

            {/* Project Highlights (the Budget bullet comes from budget + duration) */}
            {selectedProposal.highlights && (
              <div className="space-y-2">
                <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block">Project Highlights</span>
                <ul className="list-disc pl-4 space-y-1.5 text-xs text-slate-600 leading-relaxed font-medium">
                  {HIGHLIGHT_ROWS.filter(({ key }) => selectedProposal.highlights?.[key]).map(({ key, label }) => (
                    <li key={key}><span className="font-bold text-slate-800">{label}:</span> {selectedProposal.highlights?.[key]}</li>
                  ))}
                  {selectedProposal.budget_line && (
                    <li><span className="font-bold text-slate-800">Budget:</span> {selectedProposal.budget_line}</li>
                  )}
                  {selectedProposal.highlights.milestones.length > 0 && (
                    <li>
                      <span className="font-bold text-slate-800">Major Milestones:</span>
                      <ul className="list-[circle] pl-4 mt-1 space-y-1">
                        {selectedProposal.highlights.milestones.map((m, i) => (
                          <li key={i}>{m.period && <span className="font-semibold text-slate-700">{m.period}: </span>}{m.text}</li>
                        ))}
                      </ul>
                    </li>
                  )}
                </ul>
              </div>
            )}
          </div>
        )}
      </Sheet>

      {compare && (
        <ProposalCompareModal
          key={compare.status}
          status={compare.status}
          message={compare.message}
          proposal={compare.proposal}
          oldKey={compare.oldKey}
          newKey={compare.newKey}
          changes={compare.changes}
          mode={compare.mode}
          fileNames={compare.fileNames}
          onClose={() => setCompare(null)}
          onSaved={handleCompareSaved}
        />
      )}

      {formMode && (
        <ProposalFormModal
          key={`${formMode}-${formMode === 'edit' ? selectedProposal?.proposal_id : 'new'}`}
          isOpen
          mode={formMode}
          proposal={formMode === 'edit' ? selectedProposal : null}
          onClose={() => setFormMode(null)}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}
